import React, { useMemo, useState } from "react";
import { BotonVolver } from "@/components/BotonVolver";
import { abrirVisor } from "@/lib/visor";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Lock, Trash2, AlertCircle, Clock, SlidersHorizontal, X, ChevronLeft } from "lucide-react";
import { buildTodosDocs, filtrarDocs, tipoDoc } from "@/lib/todosDocs";
import { porFechaDesc } from "@/lib/archivo";
import { Carpeta, Migas } from "@/pages/Archivo";
import { DocRow } from "@/components/documentos/DocRow";
import { FiltrosSheet } from "@/components/documentos/FiltrosSheet";
import { AsignarSheet } from "@/components/documentos/AsignarSheet";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { Sheet } from "@/pages/Familia";
import { Cabecera, VerTodos, AREAS_PERSONA } from "@/components/documentos/Navegacion";
import { Avatar } from "@/components/personas/Avatar";
import { formatApiErrorDetail } from "@/lib/api";

const ACCESOS = [
  { id: "atencion", label: "Requiere atención", to: "/asuntos?estado=attention", icon: AlertCircle, color: "#B91C1C", bg: "#FEF2F2" },
  { id: "proximamente", label: "Próximamente", to: "/asuntos?estado=upcoming", icon: Clock, color: "#B45309", bg: "#FFFBEB" },
  { id: "papelera", label: "Papelera", to: "/papelera", icon: Trash2, color: "#4A5550", bg: "#F2EDE4" },
];

export default function Documentos() {
  const navigate = useNavigate();
  const data = useData();
  const { getDocumentoBlobUrl, eliminarDocumento, restaurarDocumento } = data;
  const [q, setQ] = useState("");
  const [opening, setOpening] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const [sp, setSp] = useSearchParams();
  const [f, setF] = useState({ quien: "", asunto: "", tipo: "", mes: "" });
  const [verFiltros, setVerFiltros] = useState(false);
  const [asignar, setAsignar] = useState(null);
  const { areas: areasBase, lista } = useMemo(() => buildTodosDocs(data), [data]);
  const areas = useMemo(() => areasBase.map((x) => (x.id === "familia" ? { ...x, label: "Familia" } : x)), [areasBase]);
  const rutasDe = useMemo(() => Object.fromEntries(lista.map((x) => [x.doc.id, x.rutas])), [lista]);

  const open = async (d) => {
    setOpening(d.id);
    try {
      abrirVisor(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir el documento.");
    } finally {
      setOpening(null);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    const docToDelete = confirmDel;
    try {
      await eliminarDocumento(docToDelete.id);
      setConfirmDel(null);
      toast.success("Documento eliminado", {
        description: docToDelete.filename,
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              await restaurarDocumento(docToDelete.id);
              toast.success("Documento restaurado");
            } catch (err) {
              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo restaurar.");
            }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar.");
    } finally {
      setBusy(false);
    }
  };

  const sub = (d) => [tipoDoc(d), d.date_label, d.size_label].filter(Boolean).join(" · ");
  const row = (d, i, rutas, pendiente) => <DocRow key={`${d.id}-${i}`} doc={d} idx={i} subtitle={sub(d)} rutas={rutas} opening={opening}
    onOpen={open} onDelete={setConfirmDel} onAsignar={pendiente ? setAsignar : null} testid={`documento-${d.id}`} />;
  const nFiltros = Object.values(f).filter(Boolean).length;
  const buscando = q.trim() || nFiltros > 0;
  const resultados = buscando ? filtrarDocs(lista, { q, ...f }) : [];

  const a = areas.find((x) => x.id === sp.get("a"));
  const areaTodos = a && sp.get("e") === "todos";
  const e = a?.entidades?.find((x) => x.id === sp.get("e"));
  const unaSeccion = e && e.seccionesDef.length === 1;
  const entTodos = e && !unaSeccion && sp.get("s") === "todos";
  const s = e?.seccionesDef.find((x) => x.id === (unaSeccion ? e.seccionesDef[0].id : sp.get("s")));
  const personaDe = (id) => (AREAS_PERSONA.includes(a?.id) ? (data.familyMembers || []).find((m) => m.id === id) : null);
  const partes = [{ label: "Todos", params: {} }];
  if (a) partes.push({ label: a.label, params: { a: a.id } });
  if (areaTodos) partes.push({ label: "Todos sus documentos", params: { a: a.id, e: "todos" } });
  if (e) partes.push({ label: e.label, params: { a: a.id, e: e.id } });
  if ((s && !unaSeccion) || entTodos) partes.push({ label: entTodos ? "Todos sus documentos" : s.label, params: { a: a.id, e: e.id, s: entTodos ? "todos" : s.id } });

  // Vistas «Todos»: mezclan documentos; cada uno muestra a quién/qué pertenece.
  const unicos = (items) => [...new Map(items.map((it) => [it.docId || it.key, it])).values()];
  const filasTodos = (items, prefijo) => porFechaDesc(unicos(items)).filter((it) => it.doc)
    .map((it, i) => row(it.doc, i, (rutasDe[it.doc.id] || []).filter((r) => r.startsWith(prefijo))));
  const sinDocs = <p className="text-center text-[#6E7A74] py-10" data-testid="documentos-apartado-vacio">Sin documentos en este apartado.</p>;

  let contenido;
  if (buscando) {
    contenido = resultados.length
      ? resultados.map((x, i) => row(x.doc, i, x.rutas, x.pendiente))
      : <p className="text-center text-[#6E7A74] py-10" data-testid="documentos-sin-resultados">No hay documentos que coincidan con tu búsqueda.</p>;
  } else if (!a) {
    contenido = areas.length
      ? areas.map((x, i) => <Carpeta key={x.id} idx={i} icon={x.icon} label={x.label} count={x.count} testid={`docs-area-${x.id}`}
          tono={x.id === "pendientes" ? "#D97706" : "#3A6B56"} onClick={() => setSp({ a: x.id })} />)
      : <p className="text-center text-[#6E7A74] py-10">No hay documentos todavía. Usa el botón «+ Añadir» para subir tu primer documento.</p>;
  } else if (a.id === "pendientes") {
    contenido = porFechaDesc(a.items).map((it, i) => row(it.doc, i, null, true));
  } else if (areaTodos) {
    contenido = filasTodos(a.entidades.flatMap((x) => Object.values(x.items).flat()), areasBase.find((x) => x.id === a.id).label);
  } else if (!e) {
    contenido = <>
      {a.entidades.map((x, i) => {
        const p = personaDe(x.id);
        return <Carpeta key={x.id} idx={i} icon={a.icon} label={x.label} count={x.count} testid={`docs-entidad-${x.id}`}
          lead={p ? <Avatar persona={p} size="md" /> : null} onClick={() => setSp({ a: a.id, e: x.id })} />;
      })}
      {<VerTodos testid="docs-ver-todos-area" label={`Ver todos los documentos de ${a.label}`} onClick={() => setSp({ a: a.id, e: "todos" })} />}
    </>;
  } else if (entTodos) {
    contenido = filasTodos(Object.values(e.items).flat(), `${areasBase.find((x) => x.id === a.id).label} › ${e.label}`);
  } else if (!s) {
    contenido = <>
      {e.seccionesDef.map((x, i) => {
        const n = e.items[x.id].length;
        return <Carpeta key={x.id} idx={i} icon={x.icon} label={x.label} count={n} vacio={n ? null : "Sin documentos"} testid={`docs-seccion-${x.id}`} onClick={() => setSp({ a: a.id, e: e.id, s: x.id })} />;
      })}
      <VerTodos testid="docs-ver-todos-entidad" label={`Ver todos los documentos de ${e.label}`} onClick={() => setSp({ a: a.id, e: e.id, s: "todos" })} />
    </>;
  } else {
    contenido = e.items[s.id].length ? porFechaDesc(e.items[s.id]).map((it, i) => row(it.doc, i)) : sinDocs;
  }

  return (
    <div className="space-y-5" data-testid="documentos-page">
      <BotonVolver porDefecto="/areas" testid="documentos-volver" />
      <header className="aldia-fade-up">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Todos tus documentos</h1>
        <p className="text-base text-[#6E7A74] mt-1" data-testid="documentos-explicacion">
          Tu archivo completo, esté como esté cada documento. En «Documentación al día» solo ves lo que ya está clasificado y sin nada pendiente.
        </p>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="documentos-accesos">
        {ACCESOS.map((x) => (
          <button key={x.id} data-testid={`documentos-acceso-${x.id}`} onClick={() => navigate(x.to)}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-xl font-semibold text-sm border transition-colors" style={{ color: x.color, background: x.bg, borderColor: x.bg }}>
            <x.icon size={16} /> {x.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA39D]" />
          <input data-testid="documentos-search" value={q} onChange={(ev) => setQ(ev.target.value)}
            placeholder="Busca: Endesa, Kia, Cody, seguro, octubre 2026…"
            className="w-full min-h-[52px] pl-12 pr-10 rounded-2xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]" />
          {q && <button data-testid="documentos-search-clear" onClick={() => setQ("")} aria-label="Borrar búsqueda" className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center"><X size={18} className="text-[#6E7A74]" /></button>}
        </div>
        <button data-testid="documentos-filtrar" onClick={() => setVerFiltros(true)}
          className={`shrink-0 inline-flex items-center gap-1.5 min-h-[52px] px-4 rounded-2xl font-semibold text-base border ${nFiltros ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#3A6B56] border-[#E5DFD5]"}`}>
          <SlidersHorizontal size={18} /> Filtrar{nFiltros ? ` (${nFiltros})` : ""}
        </button>
      </div>

      {buscando
        ? <p className="text-sm font-semibold text-[#4A5550]" data-testid="documentos-num-resultados">{resultados.length} {resultados.length === 1 ? "resultado" : "resultados"} · los más recientes primero</p>
        : a && (
          <div className="flex items-center gap-3">
            <button data-testid="documentos-back" onClick={() => setSp(partes[partes.length - 2].params)} aria-label="Volver" className="w-11 h-11 rounded-xl bg-[#F2EDE4] flex items-center justify-center shrink-0"><ChevronLeft size={22} className="text-[#3A6B56]" /></button>
            <Migas partes={partes} onGo={setSp} />
          </div>
        )}

      {!buscando && a && a.id !== "pendientes" && <Cabecera area={a} ent={e} persona={e && personaDe(e.id)} seccion={!unaSeccion && s} todos={areaTodos || entTodos} />}
      <div className="space-y-3" data-testid="documentos-list">{contenido}</div>

      <div className="flex items-center gap-2 bg-[#E8F0EC] rounded-xl px-4 py-3">
        <Lock size={16} className="text-[#3A6B56] shrink-0" />
        <p className="text-sm text-[#2E5645]">Tus documentos son privados. Nunca se comparten ni se exponen públicamente.</p>
      </div>

      {asignar && <AsignarSheet doc={asignar} onClose={() => setAsignar(null)} />}
      {verFiltros && <FiltrosSheet f={f} setF={setF} areas={areas} lista={lista} total={filtrarDocs(lista, { q, ...f }).length} onClose={() => setVerFiltros(false)} />}

      {confirmDel && (
        <Sheet onClose={() => setConfirmDel(null)} testid="documento-confirm-delete">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Enviar a la papelera?</h2>
          <p className="text-base text-[#6E7A74] mt-2">
            «{confirmDel.filename}» se moverá a la papelera. Podrás restaurarlo durante 30 días antes de su borrado definitivo.
          </p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmDel(null)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="documento-confirm-delete-btn" onClick={doDelete} disabled={busy} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{busy ? "Eliminando…" : "Eliminar"}</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
